import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Dropdown, Spinner } from "react-bootstrap";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaCheckCircle,
  FaDownload,
  FaFileInvoiceDollar,
  FaHistory,
  FaMoon,
  FaPencilAlt,
  FaPlus,
  FaPrint,
  FaRegCreditCard,
  FaStickyNote,
  FaTimesCircle,
  FaUser,
  FaUserFriends,
  FaWallet,
} from "react-icons/fa";

import {
  getHotelById,
  getHotelProducts,
  getHotelServices,
  getReservationById,
  resolveHotelImage,
  updateContent,
  updateReservationWithHistory,
  uploadImage,
} from "../../../FIREBASECONFIG";
import {
  HISTORY_EVENT,
  describeActor,
  statusChangeHistoryEntry,
} from "./reservationHistory";
import printReactElement from "./printDocument";
import { calcStay } from "./reservationExportUtils";
import ReservationChargesPanel from "./ReservationChargesPanel";
import ReservationInvoiceDocument from "./ReservationInvoiceDocument";
import ReservationPaymentsPanel from "./ReservationPaymentsPanel";
import {
  BORDER,
  DANGER,
  GREEN,
  MUTED,
  PAYMENT_LABELS,
  RESERVATION_TYPE,
  STATUS_META,
  buildPaymentSummary,
  computeMoney,
  createDefaultSettlement,
  createEmptyPaymentEntry,
  createPaymentDraft,
  formatClock,
  formatDate,
  formatDateTime,
  formatMoney,
  formatPaymentMode,
  getReservationNo,
  getFileName,
  getStatusActions,
  normalizeType,
  nowLocalDateTime,
  parseBookingNotes,
  joinBookingNotes,
  renderValue,
  sumServices,
  toDateTimeLocal,
  toMoment,
} from "./reservationViewUtils";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "rooms", label: "Rooms & Rates" },
  { key: "payments", label: "Payments" },
  { key: "notes", label: "Notes" },
  { key: "history", label: "History" },
];

const createEmptyServiceDraft = () => ({
  type: "",
  item: "",
  desc: "",
  quantity: 1,
  rate: 0,
  tax: 0,
});

const StatusBadge = ({ meta, size = "sm" }) => (
  <span
    style={{
      background: meta.background,
      color: meta.color,
      borderRadius: 999,
      padding: size === "sm" ? "4px 12px" : "6px 16px",
      fontSize: size === "sm" ? 12 : 13,
      fontWeight: 600,
      whiteSpace: "nowrap",
    }}
  >
    {meta.label}
  </span>
);

const SectionHeading = ({ icon: Icon, title }) => (
  <div className="d-flex align-items-center gap-2 mb-3">
    <Icon style={{ color: GREEN, fontSize: 15 }} />
    <h5 className="mb-0" style={{ fontSize: 16, fontWeight: 600 }}>
      {title}
    </h5>
  </div>
);

const DetailItem = ({ label, value, children }) => (
  <div className="mb-3">
    <div style={{ color: MUTED, fontSize: 13, marginBottom: 4 }}>{label}</div>
    <div style={{ fontWeight: 500, wordBreak: "break-word" }}>
      {children || renderValue(value)}
    </div>
  </div>
);

const SummaryTile = ({ icon: Icon, label, value, hint }) => (
  <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
    <div
      style={{
        width: 34,
        height: 34,
        borderRadius: 8,
        background: "#eaf7f3",
        color: GREEN,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontSize: 15,
      }}
    >
      <Icon />
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ color: MUTED, fontSize: 13 }}>{label}</div>
      <div style={{ fontWeight: 600 }}>{value}</div>
      {hint ? <div style={{ color: MUTED, fontSize: 12 }}>{hint}</div> : null}
    </div>
  </div>
);

const SummaryRow = ({ label, value, strong }) => (
  <div className="d-flex justify-content-between align-items-center py-2">
    <span
      style={{
        color: strong ? "#212529" : MUTED,
        fontWeight: strong ? 600 : 400,
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontWeight: strong ? 700 : 500,
        color: strong ? GREEN : "#212529",
        fontSize: strong ? 17 : 15,
      }}
    >
      {value}
    </span>
  </div>
);

/** A negative remaining balance means the hotel owes the guest a refund. */
const RemainingRow = ({ remaining }) => (
  <div className="d-flex justify-content-between align-items-center py-2">
    <span style={{ fontWeight: 600 }}>Remaining</span>
    <span
      style={{
        fontWeight: 700,
        fontSize: 17,
        color: remaining === 0 ? GREEN : DANGER,
      }}
    >
      {formatMoney(Math.abs(remaining))}
      {remaining < 0 ? " (Refund)" : ""}
    </span>
  </div>
);

const Divider = () => (
  <hr
    style={{ borderTop: `1px solid ${BORDER}`, opacity: 1, margin: "22px 0" }}
  />
);

const QuickActionButton = ({
  icon: Icon,
  label,
  onClick,
  variant = "light",
  disabled,
  title,
}) => {
  const styles = {
    primary: { background: GREEN, borderColor: GREEN, color: "#fff" },
    warning: { background: "#fff", borderColor: "#f0c36d", color: "#a2751a" },
    light: { background: "#fff", borderColor: BORDER, color: "#495057" },
  }[variant];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="d-flex align-items-center justify-content-center gap-2 w-100"
      style={{
        ...styles,
        border: `1px solid ${styles.borderColor}`,
        borderRadius: 8,
        padding: "10px 12px",
        fontWeight: 500,
        fontSize: 14,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <Icon style={{ fontSize: 14 }} />
      <span>{label}</span>
    </button>
  );
};

const hydratePayment = (entry) => ({
  ...createEmptyPaymentEntry(),
  ...entry,
  amount: entry?.amount ?? "",
  recordedAt: toDateTimeLocal(entry?.recordedAt),
});

const ReservationView = () => {
  const history = useHistory();
  const location = useLocation();
  const reservationId = new URLSearchParams(location.search).get("id") || "";

  const [reservation, setReservation] = useState(
    location.state?.reservation || null,
  );
  const [loading, setLoading] = useState(!location.state?.reservation);
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [hotel, setHotel] = useState(null);

  const [payments, setPayments] = useState({
    advance: createEmptyPaymentEntry(),
    installment: createEmptyPaymentEntry(),
    final: createEmptyPaymentEntry(),
  });
  const [settlement, setSettlement] = useState(createDefaultSettlement);
  const [paymentDraft, setPaymentDraft] = useState(() =>
    createPaymentDraft("advance"),
  );
  const [editingPaymentType, setEditingPaymentType] = useState(null);
  const [draftReceiptFile, setDraftReceiptFile] = useState(null);

  const [addedServices, setAddedServices] = useState([]);
  const [savedServices, setSavedServices] = useState([]);
  const [totalServicesPrice, setTotalServicesPrice] = useState(0);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [itemOptions, setItemOptions] = useState([]);
  const [newItem, setNewItem] = useState(createEmptyServiceDraft);
  const [editingServiceIndex, setEditingServiceIndex] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");

  /** Mirror the reservation document into the editable local state. */
  const hydrate = useCallback((data) => {
    setReservation(data);

    const storedPayments = data?.payments || {};
    setPayments({
      advance: hydratePayment(storedPayments.advance),
      installment: hydratePayment(storedPayments.installment),
      final: hydratePayment(storedPayments.final),
    });
    setSettlement({
      ...createDefaultSettlement(),
      ...storedPayments.settlement,
      amount: storedPayments.settlement?.amount ?? "",
      recordedAt: toDateTimeLocal(storedPayments.settlement?.recordedAt),
    });

    const services = data?.addedServices || [];
    setAddedServices(services);
    setSavedServices(services);
    setTotalServicesPrice(data?.totalServicesPrice || 0);

    // Advance is captured at booking time, so check-in defaults to the next stage.
    const defaultType =
      normalizeType(data?.reservationType) === RESERVATION_TYPE.CHECKED_IN
        ? "installment"
        : "advance";
    setPaymentDraft(createPaymentDraft(defaultType));
    setEditingPaymentType(null);
    setDraftReceiptFile(null);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!reservationId) {
        setLoading(false);
        return;
      }
      try {
        const data = await getReservationById(reservationId);
        if (cancelled) return;
        if (data) hydrate(data);
        else setReservation(null);
      } catch (error) {
        console.error("Error fetching reservation:", error);
        if (!cancelled) {
          setFeedback({
            type: "danger",
            message: "Could not load this reservation.",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [reservationId, hydrate]);

  /**
   * The hotel drives the charges picker (services and products are per hotel)
   * and supplies the letterhead details the printed invoice needs.
   */
  useEffect(() => {
    let cancelled = false;
    const hotelId = reservation?.hotelId;
    if (!hotelId) return undefined;

    const loadCatalogue = async () => {
      try {
        const [services, products, hotelDoc] = await Promise.all([
          getHotelServices(hotelId),
          getHotelProducts(hotelId),
          getHotelById(hotelId),
        ]);
        if (cancelled) return;
        setServiceTypes(services || []);
        setAllItems(products || []);
        setHotel(hotelDoc);
      } catch (error) {
        console.error("Error fetching hotel services/products:", error);
      }
    };

    loadCatalogue();
    return () => {
      cancelled = true;
    };
  }, [reservation?.hotelId]);

  const reservationType = normalizeType(reservation?.reservationType);
  const statusMeta = STATUS_META[reservationType];
  const isCheckedIn = reservationType === RESERVATION_TYPE.CHECKED_IN;
  const isReadOnly =
    reservationType === RESERVATION_TYPE.CHECKED_OUT ||
    reservationType === RESERVATION_TYPE.CANCELLED ||
    reservationType === RESERVATION_TYPE.NO_SHOW;
  const canEditCharges = isCheckedIn;

  /** Once checked in the advance is history, so it drops out of the picker. */
  useEffect(() => {
    if (isCheckedIn && paymentDraft.type === "advance" && !editingPaymentType) {
      setPaymentDraft((prev) => ({ ...prev, type: "installment" }));
    }
  }, [isCheckedIn, paymentDraft.type, editingPaymentType]);

  const money = useMemo(
    () =>
      computeMoney({ reservation, payments, settlement, totalServicesPrice }),
    [reservation, payments, settlement, totalServicesPrice],
  );

  const paymentEntries = useMemo(() => {
    const all = { ...payments, settlement };
    return ["advance", "installment", "final", "settlement"]
      .map((key) => ({ key, ...(all[key] || {}) }))
      .filter((entry) => Number(entry.amount || 0) > 0);
  }, [payments, settlement]);

  const primaryPayment = paymentEntries.find(
    (entry) => entry.key !== "settlement",
  );

  const servicesDirty = useMemo(
    () => JSON.stringify(addedServices) !== JSON.stringify(savedServices),
    [addedServices, savedServices],
  );

  const nights = useMemo(() => {
    if (!reservation) return 0;
    const stored = Number(reservation.noOfDays || 0);
    if (stored > 0) return stored;
    const calculated = calcStay(reservation.checkIn, reservation.checkOut);
    return Number.isFinite(calculated) && calculated > 0 ? calculated : 0;
  }, [reservation]);

  /** Every write resends `image` because updateContent always overwrites it. */
  const persist = useCallback(
    async (fields) => {
      await updateContent("reservations", "", reservation.id, {
        image: reservation.image || "",
        ...fields,
      });
    },
    [reservation],
  );

  const bookingNotesList = useMemo(
    () => parseBookingNotes(reservation?.bookingNotes),
    [reservation?.bookingNotes],
  );

  const handleAddNote = async () => {
    const text = noteDraft.trim();
    if (!text) {
      setFeedback({ type: "danger", message: "Enter a note before adding." });
      return;
    }
    if (!reservation?.id) return;

    const bookingNotes = joinBookingNotes([...bookingNotesList, text]);

    setSaving(true);
    setFeedback(null);
    try {
      await persist({ bookingNotes });
      setReservation((prev) => (prev ? { ...prev, bookingNotes } : prev));
      setNoteDraft("");
      setFeedback({ type: "success", message: "Note added." });
    } catch (error) {
      console.error("Error adding booking note:", error);
      setFeedback({ type: "danger", message: "Failed to add the note." });
    } finally {
      setSaving(false);
    }
  };

  const uploadReceipt = async (file, existingUrl = "") => {
    if (!file) return existingUrl || "";
    return uploadImage(file, "payment_receipts");
  };

  const resetPaymentDraft = (type = paymentDraft.type) => {
    const nextType = isCheckedIn && type === "advance" ? "installment" : type;
    setPaymentDraft(createPaymentDraft(nextType));
    setEditingPaymentType(null);
    setDraftReceiptFile(null);
  };

  const handleEditPayment = (paymentType) => {
    const entry =
      paymentType === "settlement"
        ? settlement
        : payments[paymentType] || createEmptyPaymentEntry();

    setPaymentDraft({
      type: paymentType,
      amount: entry.amount ?? "",
      mode: entry.mode || "cash",
      remarks: entry.remarks || "",
      recordedAt: toDateTimeLocal(entry.recordedAt) || nowLocalDateTime(),
    });
    setEditingPaymentType(paymentType);
    setDraftReceiptFile(null);
  };

  /**
   * One row per payment type: a type that already holds an amount has to be
   * edited rather than added again, and the amount must be positive.
   */
  const handleSavePayment = async () => {
    if (!reservation?.id) return;

    const type = paymentDraft.type;
    const amount = Number(paymentDraft.amount || 0);
    const wasEditing = Boolean(editingPaymentType);

    if (amount <= 0) {
      setFeedback({
        type: "danger",
        message: "Please enter a payment amount greater than 0.",
      });
      return;
    }

    const alreadyExists =
      type === "settlement"
        ? Number(settlement.amount || 0) > 0
        : Number(payments[type]?.amount || 0) > 0;

    if (!wasEditing && alreadyExists) {
      setFeedback({
        type: "danger",
        message: `${PAYMENT_LABELS[type]} already exists. Use Edit on that row to update it.`,
      });
      return;
    }

    setSaving(true);
    setFeedback(null);
    try {
      const depositAt = paymentDraft.recordedAt || nowLocalDateTime();
      let nextPayments = { ...payments };
      let nextSettlement = { ...settlement };

      if (type === "settlement") {
        const receiptUrl = await uploadReceipt(
          draftReceiptFile,
          settlement.receiptUrl,
        );
        nextSettlement = {
          ...settlement,
          enabled: true,
          amount,
          mode: paymentDraft.mode || "cash",
          remarks: paymentDraft.remarks || "",
          receiptUrl,
          recordedAt: depositAt,
        };
      } else {
        const receiptUrl = await uploadReceipt(
          draftReceiptFile,
          payments[type]?.receiptUrl,
        );
        nextPayments = {
          ...payments,
          [type]: {
            ...createEmptyPaymentEntry(),
            ...payments[type],
            amount,
            mode: paymentDraft.mode || "cash",
            remarks: paymentDraft.remarks || "",
            receiptUrl,
            recordedAt: depositAt,
          },
        };
      }

      const settlementAmount = Number(nextSettlement.amount || 0);
      const paymentPayload = {
        ...nextPayments,
        settlement: {
          ...nextSettlement,
          enabled: settlementAmount > 0,
          amount: settlementAmount,
        },
      };

      const nextMoney = computeMoney({
        reservation,
        payments: nextPayments,
        settlement: nextSettlement,
        totalServicesPrice,
      });
      const paymentSummary = buildPaymentSummary(nextMoney);

      await persist({ payments: paymentPayload, paymentSummary });

      setPayments(nextPayments);
      setSettlement(paymentPayload.settlement);
      setReservation((prev) =>
        prev ? { ...prev, payments: paymentPayload, paymentSummary } : prev,
      );
      resetPaymentDraft(type);
      setFeedback({
        type: "success",
        message: wasEditing ? "Payment updated." : "Payment added.",
      });
    } catch (error) {
      console.error("Error saving payment:", error);
      setFeedback({ type: "danger", message: "Failed to save the payment." });
    } finally {
      setSaving(false);
    }
  };

  const handleServiceTypeChange = (typeId) => {
    setNewItem((prev) => ({
      ...prev,
      type: typeId,
      item: "",
      desc: "",
      rate: 0,
    }));
    setItemOptions(allItems.filter((product) => product.typeId === typeId));
  };

  const handleServiceItemChange = (productId) => {
    const product = itemOptions.find((item) => item.id === productId);
    setNewItem((prev) => ({
      ...prev,
      item: product ? product.id : "",
      desc: product?.desc || "",
      rate: product?.price || 0,
    }));
  };

  const resetServiceDraft = () => {
    setNewItem(createEmptyServiceDraft());
    setItemOptions([]);
    setEditingServiceIndex(null);
  };

  /** Adds a new charge line, or replaces the one being edited. */
  const handleSaveService = () => {
    if (!newItem.type || !newItem.item) {
      setFeedback({ type: "danger", message: "Please fill Type & Item." });
      return;
    }

    const selectedType = serviceTypes.find((type) => type.id === newItem.type);
    const selectedItem = itemOptions.find((item) => item.id === newItem.item);
    if (!selectedType || !selectedItem) {
      setFeedback({
        type: "danger",
        message: "That service is no longer available for this hotel.",
      });
      return;
    }

    const entry = {
      type: selectedType.type,
      typeId: selectedType.id,
      item: selectedItem.name,
      itemId: selectedItem.id,
      desc: newItem.desc || selectedItem.desc || "",
      quantity: Number(newItem.quantity || 0),
      rate: Number(newItem.rate || selectedItem.price || 0),
      tax: Number(newItem.tax || 0),
    };

    const nextServices =
      editingServiceIndex === null
        ? [...addedServices, entry]
        : addedServices.map((service, index) =>
            index === editingServiceIndex ? entry : service,
          );

    setAddedServices(nextServices);
    setTotalServicesPrice(sumServices(nextServices));
    resetServiceDraft();
    setFeedback(null);
  };

  const handleEditService = (index) => {
    const service = addedServices[index];
    if (!service) return;

    setItemOptions(
      allItems.filter((product) => product.typeId === service.typeId),
    );
    setNewItem({
      type: service.typeId || "",
      item: service.itemId || "",
      desc: service.desc || "",
      quantity: Number(service.quantity || 1),
      rate: Number(service.rate || 0),
      tax: Number(service.tax || 0),
    });
    setEditingServiceIndex(index);
  };

  const handleDeleteService = (index) => {
    const nextServices = addedServices.filter((_, i) => i !== index);
    setAddedServices(nextServices);
    setTotalServicesPrice(sumServices(nextServices));
    if (editingServiceIndex !== null) resetServiceDraft();
  };

  const saveServices = async ({ silent = false } = {}) => {
    if (!reservation?.id) return false;

    const total = sumServices(addedServices);
    setSaving(true);
    if (!silent) setFeedback(null);
    try {
      const paymentSummary = buildPaymentSummary(
        computeMoney({
          reservation,
          payments,
          settlement,
          totalServicesPrice: total,
        }),
      );

      await persist({
        addedServices,
        totalServicesPrice: total,
        paymentSummary,
      });

      setSavedServices(addedServices);
      setTotalServicesPrice(total);
      setReservation((prev) =>
        prev
          ? {
              ...prev,
              addedServices,
              totalServicesPrice: total,
              paymentSummary,
            }
          : prev,
      );
      if (!silent) setFeedback({ type: "success", message: "Charges saved." });
      return true;
    } catch (error) {
      console.error("Error saving charges:", error);
      setFeedback({ type: "danger", message: "Failed to save the charges." });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (nextType, successMessage, extraFields = {}) => {
    if (!reservation?.id) return;
    setSaving(true);
    setFeedback(null);
    try {
      const entry = statusChangeHistoryEntry({
        from: reservation.reservationType,
        to: nextType,
      });
      await updateReservationWithHistory(
        reservation.id,
        { reservationType: nextType, ...extraFields },
        entry,
      );
      setReservation((prev) =>
        prev
          ? {
              ...prev,
              reservationType: nextType,
              ...extraFields,
              history: [...(prev.history || []), entry],
            }
          : prev,
      );
      setFeedback({ type: "success", message: successMessage });
    } catch (error) {
      console.error("Error updating reservation status:", error);
      setFeedback({
        type: "danger",
        message: "Failed to update the reservation status.",
      });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Check-out is only allowed once the guest is fully settled: an underpayment
   * blocks it, an overpayment must be refunded through a settlement of exactly
   * that amount, and a settlement cannot linger once payments already balance.
   */
  const handleStatusAction = async (action) => {
    if (action.value !== RESERVATION_TYPE.CHECKED_OUT) {
      await updateStatus(action.value, action.message);
      return;
    }

    const {
      amountCharged,
      collected,
      balanceAmount,
      settlement: refund,
    } = money;

    if (balanceAmount > 0) {
      setFeedback({
        type: "danger",
        message: `Cannot check out. Total amount is ${formatMoney(
          amountCharged,
        )} but only ${formatMoney(collected)} collected. Balance due: ${formatMoney(
          balanceAmount,
        )}.`,
      });
      return;
    }

    if (balanceAmount < 0) {
      const refundDue = Math.abs(balanceAmount);
      if (refund !== refundDue) {
        setFeedback({
          type: "danger",
          message: `Customer overpaid by ${formatMoney(
            refundDue,
          )}. Record a settlement (refund) of exactly ${formatMoney(
            refundDue,
          )} before check out.`,
        });
        return;
      }
    } else if (refund > 0) {
      setFeedback({
        type: "danger",
        message:
          "Payments already match the total amount. Clear settlement before check out.",
      });
      return;
    }

    // Pending charges are persisted first, exactly as the invoice screen did.
    if (servicesDirty) {
      const saved = await saveServices({ silent: true });
      if (!saved) return;
    }

    await updateStatus(action.value, action.message, {
      paymentSummary: buildPaymentSummary(money),
    });
  };

  const goToEdit = () => {
    history.push("/Reservations", { editReservationId: reservation?.id });
  };

  /** Mirrors the old invoice's "Save and Print": persist first, then print. */
  const handleSaveAndPrint = async () => {
    if (servicesDirty) {
      const saved = await saveServices({ silent: true });
      if (!saved) return;
    }

    setPrinting(true);
    try {
      await printReactElement(
        <ReservationInvoiceDocument
          reservation={reservation}
          hotel={hotel}
          hotelImage={resolveHotelImage(hotel, reservation.image)}
          money={money}
          payments={payments}
          settlement={settlement}
          addedServices={addedServices}
          nights={nights}
          printedAt={nowLocalDateTime()}
        />,
        { title: `Invoice ${getReservationNo(reservation)}` },
      );
    } catch (error) {
      console.error("Error printing invoice:", error);
      setFeedback({
        type: "danger",
        message: "Could not open the invoice for printing.",
      });
    } finally {
      setPrinting(false);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-body d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="card">
        <div className="card-body text-center py-5">
          <h5 className="mb-2">Reservation not found</h5>
          <p className="text-muted mb-3">
            This reservation may have been deleted, or the link is missing an
            id.
          </p>
          <Link to="/Reservations" className="btn btn-secondary">
            Back to Reservations
          </Link>
        </div>
      </div>
    );
  }

  const statusActions = getStatusActions(reservationType);

  /** Check-out stays blocked while anything is still owed or owed back. */
  const isBlockedAction = (action) =>
    action.value === RESERVATION_TYPE.CHECKED_OUT &&
    money.remainingBalance !== 0;

  const blockedReason = (action) =>
    isBlockedAction(action)
      ? `Settle remaining balance of ${formatMoney(
          Math.abs(money.remainingBalance),
        )} before check out`
      : undefined;

  const guestName = [reservation.firstName, reservation.lastName]
    .filter(Boolean)
    .join(" ");

  const chargesReadOnlyReason = isReadOnly
    ? "This reservation is closed, so charges can no longer be changed."
    : "Services can only be charged once the guest has checked in.";

  const renderOverview = () => (
    <div className="card">
      <div className="card-body">
        <SectionHeading icon={FaUser} title="Guest & Booking Details" />
        <div className="row">
          <div className="col-md-6">
            <DetailItem
              label="Booking Type"
              value={reservation.reservationType}
            />
          </div>
          <div className="col-md-6">
            <DetailItem label="Email" value={reservation.email} />
          </div>
          <div className="col-md-6">
            <DetailItem label="Booking Source" value={reservation.source} />
          </div>
          <div className="col-md-6">
            <DetailItem label="Address" value={reservation.address} />
          </div>
          <div className="col-md-6">
            <DetailItem label="Guest Name" value={guestName} />
          </div>
          <div className="col-md-6">
            <DetailItem label="Adults" value={reservation.adults ?? 0} />
          </div>
          <div className="col-md-6">
            <DetailItem label="CNIC / Passport" value={reservation.passport} />
          </div>
          <div className="col-md-6">
            <DetailItem label="Children" value={reservation.children ?? 0} />
          </div>
          <div className="col-md-6">
            <DetailItem label="Mobile" value={reservation.mobile} />
          </div>
        </div>

        <Divider />

        <SectionHeading icon={FaCalendarAlt} title="Stay Information" />
        <div className="row">
          <div className="col-md-3">
            <DetailItem label="Hotel" value={reservation.hotelName} />
          </div>
          <div className="col-md-3">
            <DetailItem label="Check-in">
              <div>{formatDate(reservation.checkIn)}</div>
              {reservation.checkInTime ? (
                <div style={{ color: MUTED, fontSize: 13, fontWeight: 400 }}>
                  {formatClock(reservation.checkInTime)}
                </div>
              ) : null}
            </DetailItem>
          </div>
          <div className="col-md-3">
            <DetailItem label="Check-out">
              <div>{formatDate(reservation.checkOut)}</div>
              {reservation.checkOutTime ? (
                <div style={{ color: MUTED, fontSize: 13, fontWeight: 400 }}>
                  {formatClock(reservation.checkOutTime)}
                </div>
              ) : null}
            </DetailItem>
          </div>
          <div className="col-md-3">
            <DetailItem
              label="No of Days"
              value={`${nights} ${nights === 1 ? "Night" : "Nights"}`}
            />
          </div>
        </div>

        <Divider />

        <SectionHeading icon={FaFileInvoiceDollar} title="Booking Summary" />
        <SummaryRow label="Total Rate" value={formatMoney(money.roomTotal)} />
        <SummaryRow
          label="Total Rate (with tax)"
          value={formatMoney(money.withTax)}
        />
        <SummaryRow
          label="Other Services"
          value={formatMoney(money.services)}
        />
        <hr
          style={{
            borderTop: `1px solid ${BORDER}`,
            opacity: 1,
            margin: "12px 0",
          }}
        />
        <SummaryRow
          label="Grand Total"
          value={formatMoney(money.amountCharged)}
          strong
        />
        <SummaryRow
          label="Collected Amount"
          value={formatMoney(money.collected)}
        />
        {money.settlement > 0 ? (
          <SummaryRow
            label={PAYMENT_LABELS.settlement}
            value={`- ${formatMoney(money.settlement)}`}
          />
        ) : null}
        <RemainingRow remaining={money.remainingBalance} />

        <Divider />

        <SectionHeading icon={FaRegCreditCard} title="Payment Information" />
        {primaryPayment ? (
          <div className="row">
            <div className="col-md-6">
              <DetailItem
                label="Payment Type"
                value={PAYMENT_LABELS[primaryPayment.key]}
              />
            </div>
            <div className="col-md-6">
              <DetailItem
                label="Payment"
                value={formatPaymentMode(primaryPayment.mode)}
              />
            </div>
            <div className="col-md-6">
              <DetailItem
                label="Amount"
                value={formatMoney(primaryPayment.amount)}
              />
            </div>
            <div className="col-md-6">
              <DetailItem label="Receipt">
                {primaryPayment.receiptUrl ? (
                  <a
                    href={primaryPayment.receiptUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="d-inline-flex align-items-center gap-2"
                    style={{ color: GREEN, fontWeight: 500 }}
                  >
                    <FaFileInvoiceDollar style={{ fontSize: 13 }} />
                    <span>{getFileName(primaryPayment.receiptUrl)}</span>
                    <FaDownload style={{ fontSize: 12 }} />
                  </a>
                ) : (
                  "-"
                )}
              </DetailItem>
            </div>
          </div>
        ) : (
          <p className="mb-0" style={{ color: MUTED }}>
            No payment has been recorded for this reservation yet.
          </p>
        )}

        <Divider />

        <SectionHeading icon={FaStickyNote} title="Booking Notes" />
        {bookingNotesList.length ? (
          <ul className="mb-0 ps-3">
            {bookingNotesList.map((note, index) => (
              <li key={`${index}-${note}`} style={{ marginBottom: 4 }}>
                {note}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mb-0" style={{ color: MUTED }}>
            No special requests or notes.
          </p>
        )}
      </div>
    </div>
  );

  const renderNotes = () => (
    <div className="card">
      <div className="card-body">
        <SectionHeading icon={FaStickyNote} title="Booking Notes" />

        {bookingNotesList.length ? (
          <ul className="mb-3 ps-3">
            {bookingNotesList.map((note, index) => (
              <li key={`${index}-${note}`} style={{ marginBottom: 8 }}>
                {note}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mb-3" style={{ color: MUTED }}>
            No special requests or notes.
          </p>
        )}

        <div className="row g-2 align-items-end">
          <div className="col">
            <label className="form-label">New note</label>
            <input
              type="text"
              className="form-control"
              placeholder="Add a booking note"
              value={noteDraft}
              disabled={saving}
              onChange={(event) => setNoteDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleAddNote();
                }
              }}
            />
          </div>
          <div className="col-auto">
            <button
              type="button"
              className="btn btn-secondary d-inline-flex align-items-center gap-2"
              disabled={saving}
              onClick={handleAddNote}
            >
              <FaPlus style={{ fontSize: 12 }} />
              {saving ? "Saving..." : "Add Note"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  /** Recorded status events, with payments and legacy records filled in around them. */
  const renderHistory = () => {
    const logged = reservation.history || [];

    const statusLabel = (value) => STATUS_META[normalizeType(value)].label;

    const loggedEvents = logged.map((entry) => {
      const actor = `by ${describeActor(entry.by)}`;

      if (entry.type === HISTORY_EVENT.CREATED) {
        return {
          label: "Reservation created",
          detail: [
            entry.source ? `Booked through ${entry.source}` : null,
            entry.to ? `Created as ${statusLabel(entry.to)}` : null,
            actor,
          ]
            .filter(Boolean)
            .join(" · "),
          at: entry.at,
        };
      }

      return {
        label: `Status changed to ${statusLabel(entry.to)}`,
        detail: [entry.from ? `From ${statusLabel(entry.from)}` : null, actor]
          .filter(Boolean)
          .join(" · "),
        at: entry.at,
      };
    });

    // Reservations made before history existed still get a creation event.
    const createdEvents = logged.some(
      (entry) => entry.type === HISTORY_EVENT.CREATED,
    )
      ? []
      : [
          {
            label: "Reservation created",
            detail: `Booked through ${reservation.source || "the dashboard"}`,
            at: reservation.createdAt || reservation.bookingDate,
          },
        ];

    const events = [
      ...createdEvents,
      ...loggedEvents,
      ...paymentEntries.map((entry) => ({
        label: `${PAYMENT_LABELS[entry.key]} recorded`,
        detail: `${formatMoney(entry.amount)} via ${formatPaymentMode(entry.mode)}`,
        at: entry.recordedAt,
      })),
    ].sort((a, b) => {
      const left = toMoment(a.at);
      const right = toMoment(b.at);
      if (!left || !right) return 0;
      return left.valueOf() - right.valueOf();
    });

    events.push({
      label: `Current status: ${statusMeta.label}`,
      detail: statusMeta.description,
      at: null,
    });

    return (
      <div className="card">
        <div className="card-body">
          <SectionHeading icon={FaHistory} title="History" />
          <p style={{ color: MUTED, fontSize: 13 }}>
            {logged.length
              ? "Status changes are recorded with the user who made them. Payment rows come from their deposit dates."
              : "Built from the dates stored on this reservation. Status changes made before history was introduced were not recorded."}
          </p>
          <div className="d-flex flex-column gap-3">
            {events.map((event, index) => (
              <div key={`${event.label}-${index}`} className="d-flex gap-3">
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: GREEN,
                    marginTop: 6,
                    flexShrink: 0,
                  }}
                />
                <div>
                  <div style={{ fontWeight: 500 }}>{event.label}</div>
                  <div style={{ color: MUTED, fontSize: 13 }}>
                    {event.detail}
                  </div>
                  {event.at ? (
                    <div style={{ color: MUTED, fontSize: 12 }}>
                      {formatDateTime(event.at)}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderTab = () => {
    switch (activeTab) {
      case "rooms":
        return (
          <ReservationChargesPanel
            roomDetails={reservation.roomDetails || []}
            noOfDays={nights}
            addedServices={addedServices}
            serviceTypes={serviceTypes}
            itemOptions={itemOptions}
            newItem={newItem}
            onNewItemChange={setNewItem}
            onTypeChange={handleServiceTypeChange}
            onItemChange={handleServiceItemChange}
            onSaveService={handleSaveService}
            onEditService={handleEditService}
            onCancelServiceEdit={resetServiceDraft}
            onDeleteService={handleDeleteService}
            onSaveServices={saveServices}
            editingServiceIndex={editingServiceIndex}
            canEditCharges={canEditCharges}
            servicesDirty={servicesDirty}
            saving={saving}
            readOnlyReason={chargesReadOnlyReason}
          />
        );
      case "payments":
        return (
          <ReservationPaymentsPanel
            payments={payments}
            settlement={settlement}
            money={money}
            draft={paymentDraft}
            onDraftChange={setPaymentDraft}
            onReceiptChange={setDraftReceiptFile}
            receiptName={draftReceiptFile?.name || ""}
            editingType={editingPaymentType}
            onEdit={handleEditPayment}
            onSave={handleSavePayment}
            onCancelEdit={() => resetPaymentDraft()}
            hideAdvance={isCheckedIn}
            saving={saving}
          />
        );
      case "notes":
        return renderNotes();
      case "history":
        return renderHistory();
      default:
        return renderOverview();
    }
  };

  return (
    <>
      {feedback ? (
        <Alert
          variant={feedback.type}
          dismissible
          onClose={() => setFeedback(null)}
        >
          {feedback.message}
        </Alert>
      ) : null}

      <div className="card">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <h3 className="mb-0" style={{ fontWeight: 700 }}>
                  {reservation.hotelName || "Reservation"} - #
                  {getReservationNo(reservation)}
                </h3>
                {/* <StatusBadge meta={statusMeta} /> */}
              </div>
              <div style={{ color: MUTED, fontSize: 13, marginTop: 6 }}>
                Created on{" "}
                {formatDateTime(
                  reservation.createdAt || reservation.bookingDate,
                )}
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              <Dropdown align="end">
                <Dropdown.Toggle
                  variant="outline-secondary"
                  disabled={saving}
                  style={{
                    borderColor: BORDER,
                    color: "#495057",
                    background: "#fff",
                  }}
                >
                  Actions
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {statusActions.length ? (
                    statusActions.map((action) => (
                      <Dropdown.Item
                        key={action.value}
                        disabled={saving || isBlockedAction(action)}
                        title={blockedReason(action)}
                        className={
                          action.variant === "warning"
                            ? "text-danger"
                            : undefined
                        }
                        onClick={() => handleStatusAction(action)}
                      >
                        {action.label}
                      </Dropdown.Item>
                    ))
                  ) : (
                    <Dropdown.Item disabled>No actions available</Dropdown.Item>
                  )}
                  <Dropdown.Item onClick={goToEdit}>
                    Edit Reservation
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item
                    disabled
                    title="Invoice download is not implemented yet"
                  >
                    Download Receipt
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              <div>
                {/* back button to reservations list */}
                <Link to="/reservations" className="btn btn-outline-secondary">
                  Back
                </Link>
              </div>
            </div>
          </div>

          <div
            className="row g-3 mt-3"
            style={{
              border: `1px solid ${BORDER}`,
              borderRadius: 10,
              padding: "16px 8px",
              margin: 0,
            }}
          >
            <div className="col-md-2 col-sm-6">
              <SummaryTile
                icon={FaCalendarAlt}
                label="Check-in"
                value={formatDate(reservation.checkIn)}
                hint={formatClock(reservation.checkInTime)}
              />
            </div>
            <div className="col-md-2 col-sm-6">
              <SummaryTile
                icon={FaCalendarAlt}
                label="Check-out"
                value={formatDate(reservation.checkOut)}
                hint={formatClock(reservation.checkOutTime)}
              />
            </div>
            <div className="col-md-2 col-sm-6">
              <SummaryTile
                icon={FaMoon}
                label="No of Nights"
                value={`${nights} ${nights === 1 ? "Night" : "Nights"}`}
              />
            </div>
            <div className="col-md-3 col-sm-6">
              <SummaryTile
                icon={FaUserFriends}
                label="Guests"
                value={`${reservation.adults || 0} ${
                  Number(reservation.adults) === 1 ? "Adult" : "Adults"
                }, ${reservation.children || 0} ${
                  Number(reservation.children) === 1 ? "Child" : "Children"
                }`}
              />
            </div>
            <div className="col-md-3 col-sm-6">
              <SummaryTile
                icon={FaWallet}
                label="Total Amount"
                value={formatMoney(money.amountCharged)}
                hint="(with tax)"
              />
            </div>
          </div>

          <div
            className="d-flex flex-wrap gap-4 mt-4"
            style={{ borderBottom: `1px solid ${BORDER}` }}
          >
            {TABS.map((tab) => {
              const active = tab.key === activeTab;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    border: "none",
                    background: "transparent",
                    padding: "0 0 10px",
                    marginBottom: -1,
                    fontWeight: active ? 600 : 500,
                    fontSize: 15,
                    color: active ? GREEN : MUTED,
                    borderBottom: `2px solid ${active ? GREEN : "transparent"}`,
                    cursor: "pointer",
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-xl-8 col-lg-7">{renderTab()}</div>

        <div className="col-xl-4 col-lg-5">
          <div className="card">
            <div className="card-body">
              <h5 className="mb-3" style={{ fontSize: 16, fontWeight: 600 }}>
                Reservation Status
              </h5>
              <StatusBadge meta={statusMeta} size="md" />
              <p className="mb-2 mt-3" style={{ color: MUTED, fontSize: 14 }}>
                {statusMeta.description}
              </p>
              <p className="mb-0" style={{ color: MUTED, fontSize: 14 }}>
                {statusMeta.hint}
              </p>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h5 className="mb-3" style={{ fontSize: 16, fontWeight: 600 }}>
                Payment Summary
              </h5>
              <SummaryRow
                label="Total Amount"
                value={formatMoney(money.amountCharged)}
              />
              <SummaryRow
                label="Collected Amount"
                value={formatMoney(money.collected)}
              />
              {money.settlement > 0 ? (
                <SummaryRow
                  label={PAYMENT_LABELS.settlement}
                  value={`- ${formatMoney(money.settlement)}`}
                />
              ) : null}
              <RemainingRow remaining={money.remainingBalance} />
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h5 className="mb-3" style={{ fontSize: 16, fontWeight: 600 }}>
                Quick Actions
              </h5>
              <div className="d-flex flex-column gap-2">
                {statusActions.length ? (
                  statusActions.map((action) => (
                    <QuickActionButton
                      key={action.value}
                      icon={
                        action.variant === "primary"
                          ? FaCheckCircle
                          : FaTimesCircle
                      }
                      label={action.label}
                      variant={action.variant}
                      disabled={saving || isBlockedAction(action)}
                      title={blockedReason(action)}
                      onClick={() => handleStatusAction(action)}
                    />
                  ))
                ) : (
                  <p className="mb-1" style={{ color: MUTED, fontSize: 14 }}>
                    No status actions are available for this reservation.
                  </p>
                )}
                <QuickActionButton
                  icon={FaPencilAlt}
                  label="Edit Reservation"
                  onClick={goToEdit}
                />
                <QuickActionButton
                  icon={FaPrint}
                  label={
                    printing ? "Preparing Invoice..." : "Save & Print Invoice"
                  }
                  disabled={saving || printing}
                  title="Saves pending charges, then opens the print dialog. Choose 'Save as PDF' there to download."
                  onClick={handleSaveAndPrint}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReservationView;
